/**
 * Telegram Web App helper utilities.
 * Wraps the global window.Telegram.WebApp with safe fallbacks for development.
 */

export function getTelegramWebApp() {
  return window?.Telegram?.WebApp || null
}

export function getTelegramUser() {
  const twa = getTelegramWebApp()
  if (twa?.initDataUnsafe?.user) return twa.initDataUnsafe.user
  // Dev fallback
  return { id: 12345, first_name: 'Dev User', username: 'devuser', is_bot: false }
}

export function getTelegramChatInfo() {
  const twa = getTelegramWebApp()
  return twa?.initDataUnsafe?.chat || null
}

export function expandApp() {
  getTelegramWebApp()?.expand()
}

export function closeMiniApp() {
  getTelegramWebApp()?.close()
}

export function showMainButton(text, onClick) {
  const twa = getTelegramWebApp()
  if (!twa) return
  twa.MainButton.setText(text)
  twa.MainButton.onClick(onClick)
  twa.MainButton.show()
}

export function hideMainButton() {
  getTelegramWebApp()?.MainButton.hide()
}

export function setMainButtonLoading(loading) {
  const twa = getTelegramWebApp()
  if (!twa) return
  if (loading) twa.MainButton.showProgress()
  else twa.MainButton.hideProgress()
}

export function showAlert(message) {
  const twa = getTelegramWebApp()
  if (twa) twa.showAlert(message)
  else window.alert(message)
}

export function showConfirm(message) {
  const twa = getTelegramWebApp()
  if (twa) return new Promise(resolve => twa.showConfirm(message, resolve))
  return Promise.resolve(window.confirm(message))
}

export function sendDataToBot(data) {
  const twa = getTelegramWebApp()
  if (twa) twa.sendData(JSON.stringify(data))
}

export function hapticImpact(style = 'medium') {
  getTelegramWebApp()?.HapticFeedback?.impactOccurred(style)
}

export function hapticNotification(type = 'success') {
  getTelegramWebApp()?.HapticFeedback?.notificationOccurred(type)
}

export function openExternalLink(url) {
  const twa = getTelegramWebApp()
  if (twa) {
    twa.openLink(url)
  } else {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}

export function isInTelegram() {
  return Boolean(window?.Telegram?.WebApp?.initData)
}

export function getThemeParams() {
  return getTelegramWebApp()?.themeParams || {}
}

export function getStartParamToken() {
  const twa = getTelegramWebApp()
  const rawStartParam = twa?.initDataUnsafe?.start_param
  if (rawStartParam) {
    return String(rawStartParam).replace(/^ip_/, '')
  }

  if (typeof window === 'undefined') return ''
  const params = new URLSearchParams(window.location.search)
  // Precedence:
  // 1) tgWebAppStartParam (Telegram WebApp launch)
  // 2) inlinePollToken (direct frontend fallback URL)
  // 3) startapp (deep-link query fallback)
  const fallbackParam = params.get('tgWebAppStartParam') || params.get('inlinePollToken') || params.get('startapp')
  return fallbackParam ? String(fallbackParam).replace(/^ip_/, '') : ''
}

export function formatDateTime(dateStr) {
  if (!dateStr) return 'N/A'
  try {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return dateStr
  }
}

export function getActivityEmoji(type) {
  const map = {
    quiz: '🧠',
    quiz_night: '🧠',
    ice_rink: '⛸️',
    skating: '⛸️',
    bowling: '🎳',
    escape_room: '🔓',
    concert: '🎵',
    sport: '⚽',
    yoga: '🧘',
    cooking: '🍳',
    art: '🎨',
    cinema: '🎬',
    theater: '🎭',
    dance: '💃',
    fitness: '🏋️',
    other: '📅'
  }
  const key = (type || 'other').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
  return map[key] || map.other
}

export function getSlotsStatus(available, total) {
  if (available === null || available === undefined) return 'unknown'
  if (available === 0) return 'none'
  if (total && available / total < 0.2) return 'low'
  return 'available'
}
